/**
 * Audit-log export for an external auditor (super_admin only).
 *
 * The export is recorded in the audit log under the person who runs it, before
 * the file is produced. CSV has no row limit; JSON stops at 10,000 rows and says
 * so, in which case narrow the filters or take the CSV.
 */
import { useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import auditLogExportService, {
  type ExportFormat,
} from '../../services/auditLogExport.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import {
  EMPTY_FORM,
  MAX_RANGE_DAYS,
  exportBlockedReason,
  toFilters,
  type ExportForm,
} from './auditLogExport.helpers';

function saveFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AuditLogExport(): JSX.Element {
  const [form, setForm] = useState<ExportForm>(EMPTY_FORM);
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const blocked = busy ? 'Preparing the file…' : exportBlockedReason(form);
  const set = (field: keyof ExportForm) =>
    (e: { target: { value: string } }): void => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const file = await auditLogExportService.exportLog(toFilters(form), format);
      saveFile(file.blob, file.filename);
      setDone(`Downloaded ${file.filename}. This export is now part of the audit log.`);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not export the audit log.'));
    } finally {
      setBusy(false);
    }
  };

  const field = { border: `1px solid ${colors.border}` };
  const labelCls = 'block text-xs font-semibold mb-1';

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Audit log export"
        subtitle="Download the audit log for an external auditor. Days are UTC and both ends are included."
      />

      <section className="rounded-2xl p-5"
               style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
        <p className="text-sm mb-4" style={{ color: colors.inkSoft }}>
          Running an export is itself recorded in the audit log, under your name, with the
          filters you chose. That entry cannot be removed afterwards.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="audit-from" className={labelCls} style={{ color: colors.inkSoft }}>
              First day
            </label>
            <input id="audit-from" type="date" value={form.from} onChange={set('from')}
                   disabled={busy} className="w-full px-3 py-2 rounded-lg text-sm" style={field} />
          </div>
          <div>
            <label htmlFor="audit-to" className={labelCls} style={{ color: colors.inkSoft }}>
              Last day
            </label>
            <input id="audit-to" type="date" value={form.to} onChange={set('to')}
                   disabled={busy} className="w-full px-3 py-2 rounded-lg text-sm" style={field} />
          </div>
        </div>
        <p className="text-xs mb-4" style={{ color: colors.muted }}>
          At most {MAX_RANGE_DAYS} days between the first and last day.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="audit-actor" className={labelCls} style={{ color: colors.inkSoft }}>
              Actor ID (optional)
            </label>
            <input id="audit-actor" type="text" value={form.actorId} onChange={set('actorId')}
                   disabled={busy} className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                   style={field} />
          </div>
          <div>
            <label htmlFor="audit-action" className={labelCls} style={{ color: colors.inkSoft }}>
              Action (optional)
            </label>
            <input id="audit-action" type="text" value={form.action} onChange={set('action')}
                   disabled={busy} className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                   style={field} />
          </div>
          <div>
            <label htmlFor="audit-target-type" className={labelCls} style={{ color: colors.inkSoft }}>
              Target type (optional)
            </label>
            <input id="audit-target-type" type="text" value={form.targetType}
                   onChange={set('targetType')} disabled={busy}
                   className="w-full px-3 py-2 rounded-lg text-sm font-mono" style={field} />
          </div>
          <div>
            <label htmlFor="audit-target" className={labelCls} style={{ color: colors.inkSoft }}>
              Target ID (optional)
            </label>
            <input id="audit-target" type="text" value={form.targetId} onChange={set('targetId')}
                   disabled={busy} className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                   style={field} />
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="audit-format" className={labelCls} style={{ color: colors.inkSoft }}>
            File type
          </label>
          <select id="audit-format" value={format} disabled={busy}
                  onChange={(e) => setFormat(e.target.value === 'json' ? 'json' : 'csv')}
                  className="px-3 py-2 rounded-lg text-sm" style={field}>
            <option value="csv">CSV (no row limit)</option>
            <option value="json">JSON (up to 10,000 rows)</option>
          </select>
        </div>

        {error && (
          <p role="alert" className="text-sm mb-3" style={{ color: colors.danger }}>{error}</p>
        )}
        {done && (
          <output className="block text-sm mb-3" style={{ color: colors.success }}>{done}</output>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            disabled={blocked !== null}
            onClick={() => { void run(); }}
            className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{ background: colors.navy, color: '#FFFFFF' }}
          >
            {busy ? 'Preparing…' : 'Export audit log'}
          </button>
          {blocked && (
            <span className="text-xs" style={{ color: colors.muted }}>{blocked}</span>
          )}
        </div>
      </section>
    </div>
  );
}
